import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Sparkles } from 'lucide-react';

const PLANS = [
  {
    key: 'free',
    name: 'Gratis',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: 'Para empezar a ordenar tus finanzas',
    features: [
      { text: '1 cuenta personal', included: true },
      { text: 'Registro de gastos e ingresos', included: true },
      { text: 'Presupuesto básico', included: true },
      { text: 'Dashboard simple', included: true },
      { text: 'Metas de ahorro', included: false },
      { text: 'Modo pareja', included: false },
      { text: 'Escáner de boletas', included: false },
      { text: 'Exportar datos', included: false },
    ],
    cta: 'Empezar gratis',
    popular: false,
  },
  {
    key: 'pro',
    name: 'Pro',
    monthlyPrice: 3990,
    yearlyPrice: 3190,
    description: 'Para quienes quieren control total',
    features: [
      { text: 'Todo lo del plan Free', included: true },
      { text: 'Cuentas ilimitadas', included: true },
      { text: 'Metas de ahorro avanzadas', included: true },
      { text: 'Reportes detallados', included: true },
      { text: 'Exportar CSV/PDF', included: true },
      { text: 'Escáner de boletas OCR', included: true },
      { text: 'Sin anuncios', included: true },
      { text: 'Soporte prioritario', included: false },
    ],
    cta: 'Empezar Pro',
    popular: true,
  },
  {
    key: 'family',
    name: 'Familia',
    monthlyPrice: 5990,
    yearlyPrice: 4790,
    description: 'Para parejas y familias',
    features: [
      { text: 'Todo lo del plan Pro', included: true },
      { text: 'Modo pareja completo', included: true },
      { text: 'Splits automáticos', included: true },
      { text: 'Metas compartidas', included: true },
      { text: 'Gastos por miembro', included: true },
      { text: 'Hasta 6 miembros', included: true },
      { text: 'Soporte prioritario 24/7', included: true },
      { text: 'Onboarding personalizado', included: true },
    ],
    cta: 'Empezar Familia',
    popular: false,
  },
];

function formatPrice(price) {
  if (price === 0) return '$0';
  return `$${price.toLocaleString('es-CL')}`;
}

export default function PricingSection2({ onCta }) {
  const { t } = useTranslation();
  const [isYearly, setIsYearly] = useState(false);

  return (
    <section id="pricing" className="py-20 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-brand-100 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold mb-4">
            PRECIOS TRANSPARENTES
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
            Elige tu plan
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-xl mx-auto mb-8">
            Empieza gratis y actualiza cuando quieras. Sin compromisos.
          </p>

          {/* Toggle */}
          <div className="inline-flex items-center gap-3 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                !isYearly ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                isYearly ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500'
              }`}
            >
              Anual
              <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold rounded-full">
                -20%
              </span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((plan) => (
            <div
              key={plan.key}
              className={`relative rounded-2xl p-6 transition-all ${
                plan.popular
                  ? 'bg-white dark:bg-gray-900 border-2 border-brand-500 shadow-xl scale-105 z-10'
                  : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-brand-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  <Sparkles size={12} />
                  MÁS POPULAR
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{plan.description}</p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-gray-900 dark:text-white">
                    {formatPrice(isYearly ? plan.yearlyPrice : plan.monthlyPrice)}
                  </span>
                  {plan.monthlyPrice > 0 && (
                    <span className="text-gray-500 dark:text-gray-400">/mes</span>
                  )}
                </div>
                {isYearly && plan.monthlyPrice > 0 && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                    Ahorras ${((plan.monthlyPrice - plan.yearlyPrice) * 12).toLocaleString('es-CL')} al año
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feat, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    {feat.included ? (
                      <Check size={16} className="text-emerald-500 shrink-0" />
                    ) : (
                      <X size={16} className="text-gray-300 dark:text-gray-700 shrink-0" />
                    )}
                    <span className={feat.included ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'}>
                      {feat.text}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={onCta}
                className={`w-full py-3 rounded-xl font-semibold transition-all ${
                  plan.popular
                    ? 'bg-brand-600 hover:bg-brand-700 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Guarantees */}
        <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <Check size={16} className="text-emerald-500" />
            Cancela cuando quieras
          </span>
          <span className="flex items-center gap-1.5">
            <Check size={16} className="text-emerald-500" />
            30 días de garantía
          </span>
          <span className="flex items-center gap-1.5">
            <Check size={16} className="text-emerald-500" />
            Pagos seguros
          </span>
        </div>
      </div>
    </section>
  );
}
